import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import companyService from '../services/companyService';
import caseService from '../services/caseService';
import newsService from '../services/newsService';
import Contact from './Contact';
import SEO from '../components/SEO';
import './About.css';

const About = () => {
    const [activeSection, setActiveSection] = useState('company-overview');
    const [companyInfo, setCompanyInfo] = useState({});
    const [casesData, setCasesData] = useState([]);
    const [newsData, setNewsData] = useState([]);
    const [isStudyInfoExpanded, setIsStudyInfoExpanded] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const menuItems = [
        { id: 'company-overview', label: '公司概况' },
        { 
            id: 'study-info', 
            label: '留学信息',
            children: [
                { id: 'study-info-cases', label: '成功案例' },
                { id: 'study-info-partners', label: '合作学校' },
                { id: 'study-info-schools', label: '学校信息' },
            ]
        },
        { id: 'announcements', label: '公告声明' },
        { id: 'privacy-policy', label: '个人信息保护政策' },
        { id: 'service-agreement', label: '服务协议' },
        { id: 'education-column', label: '教育信息专栏' },
        { id: 'customer-service', label: '客户服务与投诉' },
    ];

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [companyData, cases, news] = await Promise.all([
                    companyService.getObjectData().catch(() => ({})),
                    caseService.getListData().catch(() => []),
                    newsService.getListData().catch(() => [])
                ]);
                if (!alive) return;
                setCompanyInfo(companyData || {});
                setCasesData(Array.isArray(cases) ? cases.filter(c => !c.isDeleted) : []);
                setNewsData(Array.isArray(news) ? news.filter(n => !n.isDeleted) : []);
            } catch (_) {
                if (!alive) return;
                setCompanyInfo({});
                setCasesData([]);
                setNewsData([]);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Handle hash navigation
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash) {
            // If hash is 'study-info', redirect to first child
            if (hash === 'study-info') {
                const studyInfoItem = menuItems.find(item => item.id === 'study-info');
                if (studyInfoItem?.children?.length > 0) {
                    const firstChildId = studyInfoItem.children[0].id;
                    navigate(`/about#${firstChildId}`, { replace: true });
                    return;
                }
            }
            setActiveSection(hash);
            // Auto-expand study-info if a sub-section is selected
            if (hash.startsWith('study-info-')) {
                setIsStudyInfoExpanded(true);
            }
        }
    }, [location.hash, navigate]);

    const handleMenuClick = (sectionId, e) => {
        if (e) {
            e.stopPropagation();
        }
        setActiveSection(sectionId);
        navigate(`/about#${sectionId}`, { replace: true });
    };

    const handleParentMenuClick = (item) => {
        if (item.children) {
            const willBeExpanded = !isStudyInfoExpanded;
            setIsStudyInfoExpanded(willBeExpanded);
            // If expanding, select first child
            if (willBeExpanded && item.children.length > 0) {
                setActiveSection(item.children[0].id);
                navigate(`/about#${item.children[0].id}`, { replace: true });
            }
            // If collapsing, don't change active section - just collapse
        } else {
            handleMenuClick(item.id);
        }
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'company-overview':
                return (
                    <div>
                        <h2 className="mb-4">公司概况</h2>
                        <div className="content-section">
                            <h4 className="mb-3">基本信息</h4>
                            <div className="info-grid">
                                <div className="info-item">
                                    <strong>中文名称：</strong>
                                    <span>{companyInfo.name || '留洋教育科技公司'}</span>
                                </div>
                                <div className="info-item">
                                    <strong>中文简称：</strong>
                                    <span>{companyInfo.shortName || '留洋'}</span>
                                </div>
                                <div className="info-item">
                                    <strong>网站地址：</strong>
                                    <span>
                                        {companyInfo.website ? (
                                            <a href={companyInfo.website} target="_blank" rel="noopener noreferrer">
                                                {companyInfo.website}
                                            </a>
                                        ) : (
                                            'https://liuyanghorizon.com'
                                        )}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <strong>企业类型：</strong>
                                    <span>有限责任公司</span>
                                </div>
                            </div>
                        </div>
                        <div className="content-section mt-4">
                            <h4 className="mb-3">自营平台名称</h4>
                            <div className="info-grid">
                                <div className="info-item">
                                    <strong>经营互联网保险业务的网站名称：</strong>
                                    <span>留洋教育科技</span>
                                </div>
                                <div className="info-item">
                                    <strong>经营互联网保险业务的网站地址：</strong>
                                    <span>
                                        {companyInfo.website ? (
                                            <a href={companyInfo.website} target="_blank" rel="noopener noreferrer">
                                                {companyInfo.website}
                                            </a>
                                        ) : (
                                            'https://liuyanghorizon.com'
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'study-info':
                // Don't display content for parent item - this should not be reached
                // If reached, return null to show empty content
                return null;

            case 'study-info-cases':
                return (
                    <div>
                        <h2 className="mb-4">成功案例</h2>
                        <div className="content-section">
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead>
                                        <tr>
                                            <th>案例标题</th>
                                            <th>日期</th>
                                            <th>摘要</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {casesData.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted">
                                                    暂无成功案例
                                                </td>
                                            </tr>
                                        ) : (
                                            casesData.map((item) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <Link 
                                                            to={`/info?type=cases&id=${item.id}&from=about`}
                                                            className="case-title-link"
                                                        >
                                                            {item.title || '-'}
                                                        </Link>
                                                    </td>
                                                    <td>{item.date || '-'}</td>
                                                    <td>{item.excerpt || '-'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            case 'study-info-partners':
                // Mock data for partner schools
                const partnerSchools = [
                    {
                        schoolName: '香港大學',
                        branches: '香港',
                        cooperationScope: '本科、研究生申请服务',
                        website: 'https://www.hku.hk',
                        contactPhone: '+852 2859 2111',
                        cooperationPeriod: '2020-01-01 至 2026-12-31'
                    },
                    {
                        schoolName: '香港中文大學',
                        branches: '香港',
                        cooperationScope: '本科、研究生申请服务',
                        website: 'https://www.cuhk.edu.hk',
                        contactPhone: '+852 3943 7000',
                        cooperationPeriod: '2020-01-01 至 2026-12-31'
                    },
                    {
                        schoolName: '香港科技大學',
                        branches: '香港',
                        cooperationScope: '本科、研究生申请服务',
                        website: 'https://www.ust.hk',
                        contactPhone: '+852 2358 6000',
                        cooperationPeriod: '2020-01-01 至 2026-12-31'
                    },
                ];
                return (
                    <div>
                        <h2 className="mb-4">合作学校</h2>
                        <div className="content-section">
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead>
                                        <tr>
                                            <th>学校名称</th>
                                            <th>已设分支机构</th>
                                            <th>业务合作范围</th>
                                            <th>学校网址</th>
                                            <th>客服电话</th>
                                            <th>合作起止日期</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {partnerSchools.map((school, idx) => (
                                            <tr key={idx}>
                                                <td>{school.schoolName}</td>
                                                <td>{school.branches}</td>
                                                <td>{school.cooperationScope}</td>
                                                <td>
                                                    <a href={school.website} target="_blank" rel="noopener noreferrer">
                                                        {school.website}
                                                    </a>
                                                </td>
                                                <td>{school.contactPhone}</td>
                                                <td>{school.cooperationPeriod}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            case 'study-info-schools':
                // Mock data for school information
                const schoolInfo = [
                    {
                        schoolName: '香港大學',
                        location: '香港',
                        programs: '本科、研究生、博士',
                        applicationDeadline: '每年12月-次年1月',
                        tuitionFee: '约140,000-180,000港币/年',
                        website: 'https://www.hku.hk'
                    },
                    {
                        schoolName: '香港中文大學',
                        location: '香港',
                        programs: '本科、研究生、博士',
                        applicationDeadline: '每年12月-次年1月',
                        tuitionFee: '约140,000-180,000港币/年',
                        website: 'https://www.cuhk.edu.hk'
                    },
                    {
                        schoolName: '香港科技大學',
                        location: '香港',
                        programs: '本科、研究生、博士',
                        applicationDeadline: '每年12月-次年1月',
                        tuitionFee: '约140,000-180,000港币/年',
                        website: 'https://www.ust.hk'
                    },
                    {
                        schoolName: '香港理工大學',
                        location: '香港',
                        programs: '本科、研究生、博士',
                        applicationDeadline: '每年12月-次年1月',
                        tuitionFee: '约140,000-180,000港币/年',
                        website: 'https://www.polyu.edu.hk'
                    },
                    {
                        schoolName: '香港城市大學',
                        location: '香港',
                        programs: '本科、研究生、博士',
                        applicationDeadline: '每年12月-次年1月',
                        tuitionFee: '约140,000-180,000港币/年',
                        website: 'https://www.cityu.edu.hk'
                    },
                ];
                return (
                    <div>
                        <h2 className="mb-4">学校信息</h2>
                        <div className="content-section">
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead>
                                        <tr>
                                            <th>学校名称</th>
                                            <th>所在地</th>
                                            <th>开设项目</th>
                                            <th>申请截止日期</th>
                                            <th>学费</th>
                                            <th>学校网址</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schoolInfo.map((school, idx) => (
                                            <tr key={idx}>
                                                <td>{school.schoolName}</td>
                                                <td>{school.location}</td>
                                                <td>{school.programs}</td>
                                                <td>{school.applicationDeadline}</td>
                                                <td>{school.tuitionFee}</td>
                                                <td>
                                                    <a href={school.website} target="_blank" rel="noopener noreferrer">
                                                        {school.website}
                                                    </a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            case 'announcements':
                return (
                    <div>
                        <h2 className="mb-4">公告声明</h2>
                        <div className="content-section">
                            <p>本页面用于发布公司重要公告和声明信息。</p>
                            <p>如有重要公告，我们将在此页面及时更新。</p>
                        </div>
                    </div>
                );

            case 'privacy-policy':
                return (
                    <div>
                        <h2 className="mb-4">个人信息保护政策</h2>
                        <div className="content-section">
                            <h5 className="mb-3">1. 信息收集</h5>
                            <p>我们收集的信息包括但不限于：</p>
                            <ul>
                                <li>您主动提供的个人信息（姓名、联系方式、邮箱等）</li>
                                <li>网站使用过程中产生的技术信息（IP地址、浏览器类型等）</li>
                            </ul>

                            <h5 className="mb-3 mt-4">2. 信息使用</h5>
                            <p>我们使用收集的信息用于：</p>
                            <ul>
                                <li>提供和改善我们的服务</li>
                                <li>与您沟通，回应您的咨询</li>
                                <li>发送服务相关通知</li>
                            </ul>

                            <h5 className="mb-3 mt-4">3. 信息保护</h5>
                            <p>我们采取适当的技术和管理措施保护您的个人信息安全，防止未经授权的访问、使用或泄露。</p>

                            <h5 className="mb-3 mt-4">4. 信息共享</h5>
                            <p>我们不会向第三方出售、交易或转让您的个人信息，除非：</p>
                            <ul>
                                <li>获得您的明确同意</li>
                                <li>法律法规要求</li>
                            </ul>

                            <h5 className="mb-3 mt-4">5. 您的权利</h5>
                            <p>您有权：</p>
                            <ul>
                                <li>访问、更正或删除您的个人信息</li>
                                <li>撤回同意</li>
                                <li>提出投诉</li>
                            </ul>

                            <h5 className="mb-3 mt-4">6. 联系我们</h5>
                            <p>如您对本政策有任何疑问，请通过以下方式联系我们：</p>
                            <ul>
                                <li>邮箱：{companyInfo.email1 || 'info@liuyanghorizon.com'}</li>
                                <li>电话：{companyInfo.chinaCustomerServiceNo || ''} (中国) {companyInfo.hkCustomerServiceNo || ''} (香港)</li>
                            </ul>
                        </div>
                    </div>
                );

            case 'service-agreement':
                return (
                    <div>
                        <h2 className="mb-4">服务协议</h2>
                        <div className="content-section">
                            <h5 className="mb-3">1. 服务范围</h5>
                            <p>留洋教育科技公司提供以下服务：</p>
                            <ul>
                                <li>留学申请咨询与规划</li>
                                <li>移民咨询服务</li>
                                <li>签证办理协助</li>
                                <li>相关后续支持服务</li>
                            </ul>

                            <h5 className="mb-3 mt-4">2. 服务条款</h5>
                            <p>客户在使用我们的服务时，需遵守以下条款：</p>
                            <ul>
                                <li>提供真实、准确的个人信息</li>
                                <li>按照约定支付服务费用</li>
                                <li>配合完成申请流程</li>
                            </ul>

                            <h5 className="mb-3 mt-4">3. 服务费用</h5>
                            <p>服务费用根据具体服务项目而定，将在服务协议中明确说明。</p>

                            <h5 className="mb-3 mt-4">4. 责任限制</h5>
                            <p>我们致力于提供专业服务，但不对申请结果做出任何保证。最终申请结果由相关院校或机构决定。</p>

                            <h5 className="mb-3 mt-4">5. 争议解决</h5>
                            <p>如发生争议，双方应友好协商解决。协商不成的，可向有管辖权的人民法院提起诉讼。</p>
                        </div>
                    </div>
                );

            case 'education-column':
                // Sort news by date descending
                const sortedNews = [...newsData].sort((a, b) => {
                    const dateA = a.date ? new Date(a.date) : new Date(0);
                    const dateB = b.date ? new Date(b.date) : new Date(0);
                    return dateB - dateA;
                });
                
                return (
                    <div>
                        <h2 className="mb-4">教育信息专栏</h2>
                        <div className="content-section">
                            {sortedNews.length === 0 ? (
                                <p className="text-muted">暂无内容</p>
                            ) : (
                                <div className="education-news-list">
                                    {sortedNews.map((item) => (
                                        <div key={item.id} className="education-news-item">
                                            <Link 
                                                to={`/info?type=news&id=${item.id}&from=about`}
                                                className="education-news-title"
                                            >
                                                {item.title || '-'}
                                            </Link>
                                            <span className="education-news-date">
                                                {item.date ? new Date(item.date).toISOString().split('T')[0] : '-'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'customer-service':
                return (
                    <div>
                        <h2 className="mb-4">客户服务与投诉</h2>
                        <div className="content-section customer-service-content">
                            <Contact />
                        </div>
                    </div>
                );

            default:
                return (
                    <div>
                        <h2 className="mb-4">公司概况</h2>
                        <p>请从左侧菜单选择查看内容。</p>
                    </div>
                );
        }
    };

    return (
        <section className="about-page py-5">
            <SEO
                title="關於我們 - 留洋教育科技公司"
                description="了解留洋教育科技公司，查看公司概况、留学信息、服务协议、个人信息保护政策等详细信息。"
                keywords="留洋教育,公司概况,留学信息,服务协议,隐私政策,客户服务"
                canonical={`https://liuyanghorizon.com${location.pathname}`}
                ogImage="https://liuyanghorizon.com/images/logo.png"
            />
            <div className="container">
                <div className="row">
                    {/* Left Sidebar Menu */}
                    <div className="col-lg-3 col-md-4 mb-4 mb-md-0">
                        <div className="about-sidebar">
                            <ul className="about-menu">
                                {menuItems.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <li
                                            className={`about-menu-item ${activeSection === item.id || (item.children && item.children.some(child => activeSection === child.id)) ? 'active' : ''} ${item.children ? 'has-children' : ''}`}
                                            onClick={() => handleParentMenuClick(item)}
                                        >
                                            {item.label}
                                            {item.children && (
                                                <i className={`fas fa-angle-${isStudyInfoExpanded ? 'up' : 'down'} ms-2`}></i>
                                            )}
                                        </li>
                                        {item.children && isStudyInfoExpanded && (
                                            <ul className="about-submenu">
                                                {item.children.map((child) => (
                                                    <li
                                                        key={child.id}
                                                        className={`about-submenu-item ${activeSection === child.id ? 'active' : ''}`}
                                                        onClick={(e) => handleMenuClick(child.id, e)}
                                                    >
                                                        {child.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Right Content Area */}
                    <div className="col-lg-9 col-md-8">
                        <div className="about-content">
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;

